import React from "react";
import {
  ContactSection,
  CTABannerSection,
  FaqSection,
  HeroSection,
  HighlightsSection,
  ProjectsSection,
  QuoteSection,
  ServiceAreaSection,
  ServicesSection,
  SiteFooter,
  SiteNavbar,
  TestimonialsSection,
} from "./components";
import {
  getContractorSectionVisibility,
  getVisibleContractorSectionLinks,
  publicSiteShowsFullQuoteForm,
  resolveHeroScrollHintHref,
} from "./sections";
import { cn } from "../../lib/utils";
import { resolveContractorSiteThemeClass } from "./siteCustomization";
import { AppToastProvider } from "../../components/feedback/AppToastProvider";
import FlashToaster from "../../components/feedback/FlashToaster";
import type { ContractorSitePageProps } from "./types";
import { usePreviewNavigationGuard } from "./usePreviewNavigationGuard";

export const ContractorSitePage: React.FC<ContractorSitePageProps> = ({
  csrfToken,
  contactPath,
  rootUrl,
  currentYear,
  business,
  projectImages = [],
  aboutImages = [],
  flash,
  seoLinks,
}) => {
  usePreviewNavigationGuard();

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const reduceMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    );
    if (reduceMotion?.matches) return;
    const root = document.documentElement;
    const previous = root.style.scrollBehavior;
    root.style.scrollBehavior = "smooth";
    return () => {
      root.style.scrollBehavior = previous;
    };
  }, []);

  const visibility = getContractorSectionVisibility(
    business,
    aboutImages.length,
  );
  const visibleSectionLinks = getVisibleContractorSectionLinks(visibility);
  const themeClassName = resolveContractorSiteThemeClass(
    business.themeVariant,
    business.hasPublishedDesignTokens,
  );

  // Show projects section if we have uploaded images, even if no text-based projects
  const hasProjectImages = projectImages.length > 0;
  const showProjects = visibility.projects || hasProjectImages;
  const showQuoteForm = publicSiteShowsFullQuoteForm(business);
  const heroScrollHintHref = resolveHeroScrollHintHref(
    visibility,
    showProjects,
  );

  return (
    <div className={cn("contractor-site-root", themeClassName)}>
      <AppToastProvider>
        <FlashToaster flash={flash} />
        <SiteNavbar business={business} sectionLinks={visibleSectionLinks} />
        <HeroSection
          business={business}
          projectImages={projectImages}
          scrollHintHref={heroScrollHintHref}
        />
        {visibility.services ? (
          <ServicesSection
            services={business.services}
            quoteFormEnabled={showQuoteForm}
          />
        ) : null}
        {visibility.highlights ? (
          <HighlightsSection
            highlights={business.highlights}
            aboutBlurb={business.aboutBlurb}
            ownerProfilePhotoUrl={business.ownerProfilePhotoUrl}
            ownerName={business.ownerName}
            aboutImages={aboutImages}
          />
        ) : null}
        {showProjects ? (
          <ProjectsSection
            projects={business.projects}
            projectImages={projectImages}
          />
        ) : null}
        {visibility.testimonials ? (
          <TestimonialsSection testimonials={business.testimonials} />
        ) : null}
        {visibility.services || showProjects ? (
          <CTABannerSection
            business={business}
            quoteFormEnabled={showQuoteForm}
          />
        ) : null}
        {visibility.serviceAreas ? (
          <ServiceAreaSection serviceAreas={business.serviceAreas} />
        ) : null}
        {visibility.faqs ? <FaqSection faqs={business.faqs} /> : null}
        {showQuoteForm ? (
          <QuoteSection
            csrfToken={csrfToken}
            contactPath={contactPath}
            business={business}
          />
        ) : (
          <ContactSection business={business} />
        )}
        <SiteFooter
          currentYear={currentYear}
          rootUrl={rootUrl}
          business={business}
          sectionLinks={visibleSectionLinks}
          seoLinks={seoLinks}
        />
      </AppToastProvider>
    </div>
  );
};
